import { ref, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useI18n } from "vue-i18n";
import * as userManagementService from "@/services/userManagementService";

export function useUserManagement() {
  const toast = useToast();
  const confirm = useConfirm();
  const { t } = useI18n();

  // Reactive data
  const users = ref([]);
  const services = ref([]);
  const groups = ref([]);
  const usersStats = ref({});
  const isLoading = ref(true);
  const selectedUsers = ref([]);
  const selectedUser = ref();
  const filters = ref({});

  // Modal states
  const editUserModal = ref(false);
  const isEditingRole = ref(false);
  const editingUser = ref(null);
  const userUpdateServiceId = ref(null);
  const updateDataPayload = ref({});
  const addMemberModal = ref(false);
  const addMemberLoading = ref(false);
  const isDeleteUser = ref(false);
  const isConfirmUpdateRole = ref(false);

  // Add member form data
  const memberFormData = ref({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    service: null,
    role: "user",
  });

  // Bulk add member data
  const bulkMemberData = ref({
    users: [],
    service: null,
    csvData: null,
  });

  // Create skeleton data for loading state
  const usersSkeleton = computed(() => {
    return Array.from({ length: 5 }, (_, index) => ({
      id: index,
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      service: { name: "" },
      role: { name: "" },
      status: "",
    }));
  });

  // Load data methods
  const loadUsers = async () => {
    try {
      isLoading.value = true;
      const response = await userManagementService.fetchUsers();
      if (response?.data) {
        users.value = response.data;
      }
    } catch (error) {
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("user_manage.failed_to_load_users"),
        life: 4000,
      });
    } finally {
      isLoading.value = false;
    }
  };

  const loadServices = async () => {
    try {
      const response = await userManagementService.fetchServices();
      if (response?.data) {
        services.value = response.data;
      }
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await userManagementService.fetchGroups();
      if (response?.data) {
        groups.value = response.data;
      }
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  };

  const loadUsersStats = async () => {
    try {
      const response = await userManagementService.getUsersStats();
      if (response?.data) {
        usersStats.value = response.data;
      }
    } catch (error) {
      console.error("Error loading users stats:", error);
    }
  };

  // Check if email exists in mail list
  const checkMailList = (email) => {
    const mailList = JSON.parse(localStorage.getItem("mailList") || "[]");
    return mailList.some((mail) => mail.toLowerCase() === email.toLowerCase());
  };

  // Add new member (single)
  const addNewMember = async () => {
    try {
      // Check if any member data is missing required fields
      const hasMissingFields = memberFormData.value.some(
        (member) =>
          !member.firstname ||
          !member.lastname ||
          !member.email ||
          !member.service
      );

      if (hasMissingFields) {
        toast.add({
          severity: "danger",
          summary: t("common.error"),
          detail: t("common.please_fill_required_fields"),
          life: 4000,
        });
        return false;
      }

      // Check if any email already exists
      const hasExistingEmail = memberFormData.value.some((member) =>
        checkMailList(member.email)
      );

      if (hasExistingEmail) {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("common.email_already_exists"),
          life: 4000,
        });
        return false;
      }

      addMemberLoading.value = true;
      const responseServices = await userManagementService.fetchServices();

      console.log(responseServices);
      // Process each member's service
      const processedMembers = await Promise.all(
        memberFormData.value.map(async (member) => {
          let serviceName = member.service;
          //console.log(serviceId)
          //return
          if (typeof member.service === "string") {
            const existingService = responseServices?.data.find(
              (s) =>
                s.name.split("_")[0].toLowerCase() ===
                member.service.toLowerCase()
            );

            if (!existingService) {
              const newService = await userManagementService.createService({
                name: member.service,
              });

              if (newService) {
                serviceName = newService.name;
                services.value.push(newService);
              } else {
                toast.add({
                  severity: "error",
                  summary: t("common.error"),
                  detail: t("user_manage.failed_to_create_service"),
                  life: 4000,
                });
                return null;
              }
            } else {
              serviceName = existingService.name;
            }
          }

          return {
            firstname: member.firstname,
            lastname: member.lastname,
            email: member.email,
            phone: member.phone,
            service: serviceName,
            role: member.role || "user",
            position: localStorage.getItem("organisation"),
          };
        })
      );

      // Filter out any failed processing
      const userData = processedMembers.filter((member) => member !== null);

      const response = await userManagementService.createSingleUser(userData);

      if (response) {
        toast.add({
          severity: "success",
          summary: t("common.success"),
          detail: t("user_manage.member_added_successfully"),
          life: 4000,
        });

        // Update mail list
        /*const mailList = JSON.parse(localStorage.getItem('mailList') || '[]')
        mailList.push(memberFormData.value.email)
        localStorage.setItem('mailList', JSON.stringify(mailList))
        
        // Send invitation
        try {
          const invitationData = {
            emails: [memberFormData.value.email],
            service: serviceId,
            message: ''
          }
          await userManagementService.sendSingleInvitation(invitationData)
        } catch (inviteError) {
          console.error('Error sending invitation:', inviteError)
        }
        */
        resetMemberForm();
        await loadUsers();
        await loadUsersStats();
        return true;
      } else {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("user_manage.failed_to_add_member"),
          life: 4000,
        });
        return false;
      }
    } catch (error) {
      console.error("Error adding member:", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail:
          error.response?.data?.message ||
          t("user_manage.failed_to_add_member"),
        life: 4000,
      });
      return false;
    } finally {
      addMemberLoading.value = false;
    }
  };

  // Add multiple members (bulk)
  const addBulkMembers = async () => {
    try {
      if (!bulkMemberData.value.users.length || !bulkMemberData.value.service) {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("common.please_fill_required_fields"),
          life: 4000,
        });
        return false;
      }

      addMemberLoading.value = true;

      // Filter out existing emails
      /*const mailList = JSON.parse(localStorage.getItem('mailList') || '[]')
      const newUsers = bulkMemberData.value.users.filter(user => 
        !mailList.some(mail => mail.toLowerCase() === user.email.toLowerCase())
      )
      
      if (newUsers.length === 0) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('user_manage.all_emails_already_exist'),
          life : 4000


        })
        return false
      }
      */
      //console.log(bulkMemberData.value)
      //return
      // Check if service exists, create if not
      //let serviceId = bulkMemberData.value.service
      //let serviceIds = bulkMemberData.value.service
      /*if (bulkMemberData.value.service.length > 0) {
        bulkMemberData.value.service.forEach(async(service) => {
          const existingService = services.value.find(s => s.name.toLowerCase().split("_")[0] === service.toLowerCase())
          if (!existingService) {
            const newService = await userManagementService.createService({
              name: service
            })
            if (newService) {
              serviceIds.push(newService.id)
              services.value.push(newService)
            } else {
              toast.add({
                severity: 'error',
                summary: t('common.error'),
                detail: t('user_manage.failed_to_create_service'),
                life : 4000


              })
              return false
            }
          } else {
            serviceIds.push(existingService.id)
          }

        })
      }*/

      // Prepare users data
      const usersData = {
        users: bulkMemberData.value.users.map((user, index) => ({
          ...user,
          //service: serviceIds[index],
          service: bulkMemberData.value.service[index],
          position: localStorage.getItem("organisation"),
        })),
      };

      const response = await userManagementService.createBulkUsers(usersData);

      if (response) {
        toast.add({
          severity: "success",
          summary: t("common.success"),
          detail: t("user_manage.bulk_members_added_successfully"),
          life: 4000,
        });

        // Update mail list
        //const newEmails = newUsers.map(user => user.email)
        //const updatedMailList = [...mailList, ...newEmails]
        //localStorage.setItem('mailList', JSON.stringify(updatedMailList))

        // Send invitations
        /*
        try {
          const invitationData = {
            emails: newEmails,
            service: serviceId,
            message: ''
          }
          await userManagementService.sendBulkInvitations(invitationData)
        } catch (inviteError) {
          console.error('Error sending invitations:', inviteError)
        }
        */
        resetBulkMemberForm();
        await loadUsers();
        return true;
      } else {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: `${t("user_manage.failed_to_add_members")}`,
          life: 4000,
        });
        return false;
      }
    } catch (error) {
      console.error("Error adding bulk members:", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: error.response?.data?.email?.[0]
          ? t("user_manage.failed_to_add_member_email_exist")
          : error.response?.data?.message ||
            t("user_manage.failed_to_add_members"),
        life: 4000,
      });
      return false;
    } finally {
      addMemberLoading.value = false;
    }
  };

  // Handle CSV upload for bulk members
  const handleMemberCSVUpload = (event) => {
    const file = event.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split("\n");
        const users = [];

        // Skip header row and process data
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line) {
            const columns = line.split(",");
            const email = columns[0]?.trim();
            const firstname = columns[1]?.trim();
            const lastname = columns[2]?.trim();
            const phone = columns[3]?.trim();

            if (email && email.includes("@") && firstname && lastname) {
              users.push({
                email,
                firstname,
                lastname,
                phone: phone || "",
                role: "user",
              });
            }
          }
        }

        bulkMemberData.value.users = users;
        bulkMemberData.value.csvData = csv;

        toast.add({
          severity: "success",
          summary: t("common.success"),
          detail: `${users.length} ${t("user_manage.users_loaded_from_csv")}`,
          life: 4000,
        });
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("user_manage.failed_to_parse_csv"),
          life: 4000,
        });
      }
    };
    reader.readAsText(file);
  };

  // Generate CSV template for members
  const generateMemberCSVTemplate = () => {
    const csvContent =
      "email,firstname,lastname,phone\nexample@email.com,John,Doe,+1234567890";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "member_template.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Reset forms
  const resetMemberForm = () => {
    memberFormData.value = {
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      service: null,
      role: "user",
    };
  };

  const resetBulkMemberForm = () => {
    bulkMemberData.value = {
      users: [],
      service: null,
      csvData: null,
    };
  };

  // User operations (existing methods)
  const deleteUser = async (user) => {
    selectedUser.value = user;
    isDeleteUser.value = true;
    console.log(selectedUser.value);
    confirm.require({
      message: `${t("common.confirm_delete_user")} ${user?.firstname || ""} ${
        user?.lastname || ""
      }`,
      header: t("common.confirm_deletion"),
      icon: "pi pi-exclamation-triangle",
      rejectProps: {
        label: t("common.cancel"),
      },
      acceptProps: {
        label: t("common.confirm"),
      },
      accept: async () => {
        selectedUser.value = null;
        try {
          await userManagementService.deleteUser(user.email);
          const mailList = JSON.parse(localStorage.getItem("mailList") || "[]");
          mailList.splice(mailList.indexOf(user.email), 1);
          localStorage.setItem("mailList", JSON.stringify(mailList));
          await loadUsers();
          toast.add({
            severity: "success",
            summary: t("common.success"),
            detail: t("user_manage.user_deleted_successfully"),
            life: 4000,
          });
          isDeleteUser.value = false;
        } catch (error) {
          toast.add({
            severity: "error",
            summary: t("common.error"),
            detail: t("user_manage.failed_to_delete_user"),
            life: 4000,
          });
          isDeleteUser.value = false;
        }
      },
      onHide: () => {
        isDeleteUser.value = false;
      },
    });
  };

  const toggleUserStatus = async (user) => {
    try {
      await userManagementService.changeUserStatus(user.id, {
        is_active: !user.is_active,
      });
      await loadUsers();
      toast.add({
        severity: "success",
        summary: t("common.success"),
        detail: user.is_active
          ? t("user_manage.user_deactivated_successfully")
          : t("user_manage.user_activated_successfully"),
        life: 4000,
      });
      /*toast.add({ 
        severity: 'error', 
        summary: t('common.error'), 
        detail: t('user_manage.failed_to_update_user_status'),
        life : 4000
      })*/
    } catch (error) {
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("user_manage.failed_to_update_user_status"),
        life: 4000,
      });
    }
  };

  const editUserRole = async (user) => {
    isConfirmUpdateRole.value = true;
    selectedUser.value = user;
    isEditingRole.value = true;
    const isManager = user.role?.name === "manager";
    const confirmMessage = isManager
      ? `Êtes vous sûr de vouloir révoquer l'accès manager de l'utilisateur ${user?.firstname || ""} ${user?.lastname || ""} ?`
      : `Êtes vous sûr de vouloir attribuer l'accès manager à l'utilisateur ${user?.firstname || ""} ${user?.lastname || ""} ?`;
    
    const successMessage = isManager
      ? `L'utilisateur ${user?.firstname || ""} ${user?.lastname || ""} a été rétrogradé au rôle de membre avec succès.`
      : `L'utilisateur ${user?.firstname || ""} ${user?.lastname || ""} a été promu au rôle de manager avec succès.`;

    confirm.require({
      message: confirmMessage,
      header: isManager ? "Confirmation de rétrogradation" : "Confirmation de promotion",
      icon: "pi pi-exclamation-triangle",
      rejectProps: {
        label: t("common.cancel"),
      },
      acceptProps: {
        label: t("common.confirm"),
      },
      accept: async () => {
        try {
          const targetRole = user.role?.name === "manager" ? "user" : "manager";
          await userManagementService.updateUserRole(user.id, targetRole);
          
          await loadUsers();
          toast.add({
            severity: "success",
            summary: t("common.success"),
            detail: successMessage,
            life: 4000,
          });
          isEditingRole.value = false;
          isConfirmUpdateRole.value = false;
          selectedUser.value = null;
        } catch (error) {
          console.error("Error updating user role:", error);
          
          const errorData = error.response?.data;
          const fieldError = errorData?.errors?.user_id?.[0];
          const generalError = errorData?.error || errorData?.message;
          
          const inactiveAccountMsg = "Le compte de l'utilisateur n'est pas actif";

          if (fieldError === inactiveAccountMsg) {
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: "Impossible de promouvoir un utilisateur inactif. Veuillez activer le compte avant de procéder.",
              life: 4000,
            });
          } else if (generalError && generalError.includes("déjà le rôle")) {
            // Handles both "déjà le rôle manager" and "déjà le rôle user"
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: generalError,
              life: 4000,
            });
          } else {
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: "Echec de la mise à jour du rôle de l'utilisateur",
              life: 4000,
            });
          }
          
          isEditingRole.value = false;
          isConfirmUpdateRole.value = false;
          selectedUser.value = null;
        }
      },
      reject: () => {
        console.log("User cancelled role update");
        isEditingRole.value = false;
        isConfirmUpdateRole.value = false;
        selectedUser.value = null;
      },
      onHide: () => {
        console.log("Role update dialog closed");
        isEditingRole.value = false;
        isConfirmUpdateRole.value = false;
        selectedUser.value = null;
      },
    });
  };

  const editUser = async (user) => {
    try {
      editUserModal.value = true;

      const response = await userManagementService.getUser(user.id);

      if (response.status === 200) {
        const userData = response.data.data;
        updateDataPayload.value = userData;

        if (updateDataPayload.value.service?.id) {
          userUpdateServiceId.value = updateDataPayload.value.service?.id;
        } else {
          userUpdateServiceId.value = services.value.find(
            (s) => s.is_default
          )?.id;
        }

        editingUser.value = user;
      } else {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("user_manage.failed_to_load_user_details"),
          life: 4000,
        });
      }
    } catch (error) {
      console.error("Error loading user for edit:", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("user_manage.failed_to_load_user_details"),
        life: 4000,
      });
    }
  };

  const saveUserChanges = async (data) => {
    try {
      const { userData, serviceId } = data;

      if (!userData.firstname || !userData.lastname || !userData.email) {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("common.please_fill_required_fields"),
          life: 4000,
        });
        return;
      }

      if (userData.service?.id !== serviceId) {
        await userManagementService.updateUserService({
          user: userData.id,
          service: serviceId,
        });
      }

      const response = await userManagementService.updateUser(
        userData.id,
        userData
      );

      if (response) {
        toast.add({
          severity: "success",
          summary: t("common.success"),
          detail: t("user_manage.user_updated_successfully"),
          life: 4000,
        });

        await loadUsers();
        editUserModal.value = false;
        editingUser.value = null;
        updateDataPayload.value = {};
      } else {
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("user_manage.failed_to_update_user"),
          life: 4000,
        });
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("user_manage.failed_to_update_user"),
        life: 4000,
      });
    }
  };

  const closeEditModal = () => {
    editUserModal.value = false;
    editingUser.value = null;
    updateDataPayload.value = {};
    userUpdateServiceId.value = null;
  };

  const changeUserService = async (user, newService) => {
    try {
      await userManagementService.updateUser(user.id, {
        service_id: newService.id,
      });
      await loadUsers();
      toast.add({
        severity: "success",
        summary: t("common.success"),
        detail: t("user_manage.user_service_updated_successfully"),
        life: 4000,
      });
    } catch (error) {
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("user_manage.failed_to_update_user_service"),
        life: 4000,
      });
    }
  };

  const resendInvitation = async (user) => {
    try {
      await userManagementService.resendActivationEmail({ email: user.email });
      toast.add({
        severity: "success",
        summary: t("common.success"),
        detail: t("user_manage.invitation_resent_successfully"),
        life: 4000,
      });
    } catch (error) {
      toast.add({
        severity: "error",
        summary: t("error"),
        detail: t("user_manage.failed_to_resend_invitation"),
        life: 4000,
      });
    }
  };

  // Add new user (handles both single and bulk based on payload)
  const addNewUser = async (payload) => {
    try {
      const { type, data, serviceId, isBulk } = payload;

      if (type === "add_member") {
        if (isBulk) {
          // Handle bulk member addition
          if (!data || !Array.isArray(data) || data.length === 0) {
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: t("common.please_provide_valid_data"),
              life: 4000,
            });
            return false;
          }

          // Set bulk data and call bulk method
          bulkMemberData.value.users = data;
          //bulkMemberData.value.service = serviceId || data[0]?.service
          await loadServices();
          bulkMemberData.value.service =
            data &&
            data.length > 0 &&
            services.value &&
            services.value.length > 0
              ? data.map(
                  (user) =>
                    user?.service ||
                    services.value.sort(
                      (a, b) => new Date(a.created_at) - new Date(b.created_at)
                    )[0]?.name
                )
              : [];
          console.log(bulkMemberData.value.service);
          return await addBulkMembers();
        } else {
          // Handle single member addition
          if (!data || !Array.isArray(data) || data.length === 0) {
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: t("common.please_provide_member_data"),
              life: 4000,
            });
            return false;
          }

          // For single member, data should contain member info
          const memberData = data;

          // Check if any member data is missing required fields
          const hasMissingFields = memberData.some(
            (member) => !member.firstname || !member.lastname || !member.email
          );

          if (hasMissingFields) {
            toast.add({
              severity: "error",
              summary: t("common.error"),
              detail: t("common.please_fill_required_fields"),
              life: 4000,
            });
            return false;
          }

          // Set member form data for all members
          memberFormData.value = memberData.map((member) => ({
            firstname: member.firstname,
            lastname: member.lastname,
            email: member.email,
            phone: member.phone || "",
            service: serviceId || member.service,
            role: member.role || "user",
          }));

          return await addNewMember();
        }
      } else if (type === "send_invitation") {
        // Handle invitation (delegate to email invitation composable)
        // This should be handled by useEmailInvitation composable
        console.warn(
          "Invitation type should be handled by useEmailInvitation composable"
        );
        return false;
      } else {
        console.warn("Unknown payload type:", type);
        toast.add({
          severity: "error",
          summary: t("common.error"),
          detail: t("common.unknown_operation_type"),
          life: 4000,
        });
        return false;
      }
    } catch (error) {
      console.error("Error in addNewUser:", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: error.message || t("common.failed_to_process_request"),
        life: 4000,
      });
      return false;
    }
  };

  return {
    // Reactive data
    users,
    services,
    groups,
    usersStats,
    isLoading,
    selectedUsers,
    filters,
    usersSkeleton,
    editUserModal,
    editingUser,
    userUpdateServiceId,
    updateDataPayload,
    addMemberModal,
    addMemberLoading,
    memberFormData,
    bulkMemberData,
    isDeleteUser,
    isEditingRole,
    isConfirmUpdateRole,
    selectedUser,

    // Load methods
    loadUsers,
    loadServices,
    loadGroups,
    loadUsersStats,

    // User operations
    deleteUser,
    editUserRole,
    toggleUserStatus,
    editUser,
    changeUserService,
    resendInvitation,
    saveUserChanges,
    closeEditModal,

    // Add member operations
    addNewUser, // Added this missing method
    addNewMember,
    addBulkMembers,
    handleMemberCSVUpload,
    generateMemberCSVTemplate,
    resetMemberForm,
    resetBulkMemberForm,
    checkMailList,
  };
}
